import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { searchUsers, getFollowing } from "@/data/requests/user-follow-requests";
import { shareTask } from "@/data/requests/shared-task-requests";
import { shareHabit } from "@/data/requests/shared-habit-requests";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Share2, Search, Check, User, Twitter, Facebook, Link2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AuthStore from "@/stores/AuthStore";

export function ShareDialog({ itemId, type, trigger, itemTitle }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("friends");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const { toast } = useToast();
  const currentUserId = AuthStore.user?.id;

  // Get following users (friends)
  const { data: followingData, isLoading: isLoadingFollowing } = useQuery({
    queryKey: ["following", currentUserId],
    queryFn: () => getFollowing(currentUserId),
    enabled: isOpen && !!currentUserId && activeTab === "friends",
  });

  // Search users
  const { data: searchResults, isLoading: isLoadingSearch } = useQuery({
    queryKey: ["users", "search", debouncedSearch],
    queryFn: () => searchUsers(debouncedSearch),
    enabled: isOpen && debouncedSearch.length > 0 && activeTab === "search",
  });

  const followingUsers = followingData?.data || [];
  const searchUsersList = searchResults?.data?.data || [];

  const shareMutation = useMutation({
    mutationFn: (userIds) => {
      if (type === "task") {
        return shareTask(itemId, userIds);
      } else {
        return shareHabit(itemId, userIds);
      }
    },
    onSuccess: () => {
      toast({ 
        title: t("common.success"),
        description: t("tasks.detail.share.successMessage", { defaultValue: `${type} shared successfully!` })
      });
      setIsOpen(false);
      setSearchQuery("");
      setSelectedUsers([]);
    },
    onError: (error) => {
      toast({ 
        title: t("common.error"),
        description: error.response?.data?.message || t("tasks.detail.share.errorMessage", { defaultValue: "Failed to share" }), 
        variant: "destructive" 
      });
    }
  });

  const toggleUser = (user) => {
    if (selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleShare = () => {
    if (selectedUsers.length === 0) {
      toast({
        title: t("common.error"),
        description: t("share.selectUser", { defaultValue: "Please select at least one user" }),
        variant: "destructive",
      });
      return;
    }
    shareMutation.mutate(selectedUsers.map(u => u.id));
  };

  const handleSocialShare = (platform) => {
    const shareUrl = `${window.location.origin}/${type}s/${itemId}`;
    const shareText = itemTitle || `${type} from Rituely`;
    
    let url = "";
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case "copy":
        navigator.clipboard.writeText(shareUrl);
        toast({
          title: t("common.success"),
          description: t("share.linkCopied", { defaultValue: "Link copied to clipboard!" }),
        });
        return;
    }
    
    if (url) {
      window.open(url, "_blank", "width=600,height=400");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon">
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("share.title", { type: type === "task" ? t("tasks.title") : t("habits.title"), defaultValue: `Share ${type}` })}</DialogTitle>
          <DialogDescription>
            {t("share.description", { defaultValue: `Share this ${type} with friends or on social media.` })}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="friends">{t("share.friends", { defaultValue: "Friends" })}</TabsTrigger>
            <TabsTrigger value="search">{t("common.search")}</TabsTrigger>
            <TabsTrigger value="social">{t("share.social", { defaultValue: "Social" })}</TabsTrigger>
          </TabsList>

          <TabsContent value="friends" className="space-y-4">
            <ScrollArea className="h-[250px] rounded-md border p-4">
              {isLoadingFollowing ? (
                <div className="text-center text-sm text-muted-foreground">{t("common.loading")}</div>
              ) : followingUsers.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t("share.noFriends", { defaultValue: "No friends yet. Search for users to follow them." })}
                </div>
              ) : (
                <div className="space-y-2">
                  {followingUsers.map((user) => {
                    const isSelected = selectedUsers.find(u => u.id === user.id);
                    return (
                      <div 
                        key={user.id} 
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-accent ${
                          isSelected ? "bg-primary/10 border border-primary" : ""
                        }`}
                        onClick={() => toggleUser(user)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback>{user.name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}</AvatarFallback>
                          </Avatar>
                          <div className="text-sm">
                            <p className="font-medium leading-none">{user.name || user.email}</p>
                            {user.email && (
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            )}
                          </div>
                        </div>
                        {isSelected && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
            {selectedUsers.length > 0 && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  {t("share.selected", { count: selectedUsers.length, defaultValue: `${selectedUsers.length} selected` })}
                </span>
                <Button 
                  size="sm" 
                  onClick={handleShare}
                  disabled={shareMutation.isPending}
                >
                  {shareMutation.isPending ? t("common.saving") : t("share.shareButton", { defaultValue: "Share" })}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="search" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("share.searchPlaceholder", { defaultValue: "Search users..." })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            <ScrollArea className="h-[250px] rounded-md border p-4">
              {isLoadingSearch ? (
                <div className="text-center text-sm text-muted-foreground">{t("common.loading")}</div>
              ) : searchUsersList.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {searchQuery ? t("common.noResults") : t("share.typeToSearch", { defaultValue: "Type to search users." })}
                </div>
              ) : (
                <div className="space-y-2">
                  {searchUsersList.map((user) => {
                    const isSelected = selectedUsers.find(u => u.id === user.id);
                    return (
                      <div 
                        key={user.id} 
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-accent ${
                          isSelected ? "bg-primary/10 border border-primary" : ""
                        }`}
                        onClick={() => toggleUser(user)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback>{user.name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}</AvatarFallback>
                          </Avatar>
                          <div className="text-sm">
                            <p className="font-medium leading-none">{user.name || user.email}</p>
                            {user.email && (
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            )}
                          </div>
                        </div>
                        {isSelected && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
            {selectedUsers.length > 0 && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">
                  {t("share.selected", { count: selectedUsers.length, defaultValue: `${selectedUsers.length} selected` })}
                </span>
                <Button 
                  size="sm" 
                  onClick={handleShare}
                  disabled={shareMutation.isPending}
                >
                  {shareMutation.isPending ? t("common.saving") : t("share.shareButton", { defaultValue: "Share" })}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="social" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4"
                onClick={() => handleSocialShare("twitter")}
              >
                <Twitter className="h-5 w-5" />
                <span>{t("share.twitter", { defaultValue: "Twitter" })}</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4"
                onClick={() => handleSocialShare("facebook")}
              >
                <Facebook className="h-5 w-5" />
                <span>{t("share.facebook", { defaultValue: "Facebook" })}</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-auto py-4 col-span-2"
                onClick={() => handleSocialShare("copy")}
              >
                <Link2 className="h-5 w-5" />
                <span>{t("share.copyLink", { defaultValue: "Copy Link" })}</span>
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
